import {
  fetchBlock,
  fetchBlockTree,
  fetchCurrentMember,
  fetchLibraryRows,
} from '@/lib/blocks/api';
import type { BlockRecord } from '@/lib/blocks/types';
import { getRecentPages, removeRecentPage } from '@/lib/navigation/recent-pages';

import { loadWorkspaceRootIds, resolveLibrarySource } from './resolve-source';
import type { LibraryFilter, LibraryRow } from './types';

function blockToLibraryRow(
  block: BlockRecord,
  meta: { source: 'family' | 'private'; parentTitle: string | null },
  visitedAt?: string | null,
): LibraryRow {
  return {
    id: block.id,
    parentId: block.parentId,
    type: block.type === 'database' ? 'database' : 'page',
    title: block.title?.trim() || (block.type === 'database' ? 'Database' : 'Untitled'),
    icon: block.icon,
    createdBy: block.createdBy ?? '',
    updatedAt: block.updatedAt,
    deletedAt: block.deletedAt,
    source: meta.source,
    parentTitle: meta.parentTitle,
    visitedAt,
  };
}

async function fetchRecentLibraryRows(): Promise<LibraryRow[]> {
  const recents = getRecentPages();
  if (recents.length === 0) {
    return [];
  }

  const member = await fetchCurrentMember();
  const roots = await fetchBlockTree(null);
  const pageRoots = roots.filter((b) => b.type === 'page');
  const { familyRootId, privateRootId } = await loadWorkspaceRootIds(pageRoots, member.id);

  const byId = new Map<string, BlockRecord>();
  for (const root of pageRoots) {
    byId.set(root.id, root);
  }

  const rows: LibraryRow[] = [];

  for (const recent of recents) {
    try {
      const block = await fetchBlock(recent.id);
      if (block.deletedAt) {
        removeRecentPage(recent.id);
        continue;
      }
      if (block.type !== 'page' && block.type !== 'database') {
        continue;
      }
      byId.set(block.id, block);
      if (block.parentId) {
        try {
          const parent = await fetchBlock(block.parentId);
          byId.set(parent.id, parent);
        } catch {
          // Parent may be inaccessible; source falls back to family.
        }
      }
      const meta = resolveLibrarySource(block, familyRootId, privateRootId, byId, member.id);
      rows.push(blockToLibraryRow(block, meta, recent.visitedAt ?? null));
    } catch {
      removeRecentPage(recent.id);
    }
  }

  return rows;
}

export async function fetchLibrary(filter: LibraryFilter): Promise<LibraryRow[]> {
  if (filter === 'favorites') {
    const rows = await fetchLibraryRows('favorites');
    return rows.map((row) => ({
      ...row,
      visitedAt: row.visitedAt ?? null,
    }));
  }

  if (filter === 'recents') {
    return fetchRecentLibraryRows();
  }

  return fetchLibraryRows(filter);
}

export function libraryEmptyMessage(filter: LibraryFilter): string {
  if (filter === 'favorites') {
    return 'Star pages from the header or sidebar menu.';
  }
  if (filter === 'recents') {
    return 'Pages you open will appear here.';
  }
  if (filter === 'trash') {
    return 'Trash is empty.';
  }
  if (filter === 'family') {
    return 'No pages in Family yet.';
  }
  if (filter === 'llm-private') {
    return 'No private pages yet. Mark a page from its header or sidebar menu.';
  }
  return 'No pages in Personal yet.';
}
